<template>
  <el-card shadow="never" class="aui-card--fill">
    <div class="ip-statistics-page">
      <!-- 统一筛选区域 -->
      <el-card class="filter-card" shadow="never">
        <el-form :inline="true" :model="queryForm" label-width="80px">
          <el-form-item label="日期范围">
            <el-date-picker
              v-model="dateRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              value-format="yyyy-MM-dd"
              :picker-options="pickerOptions"
              @change="onDateRangeChange"
            />
          </el-form-item>

          <el-form-item label="页面类型">
            <el-checkbox-group v-model="queryForm.pageTypes">
              <el-checkbox
                v-for="option in pageTypeOptions"
                :key="option.value"
                :label="option.value"
              >
                {{ option.label }}
              </el-checkbox>
            </el-checkbox-group>
          </el-form-item>

          <el-form-item label="统计指标">
            <el-checkbox-group v-model="queryForm.metricTypes">
              <el-checkbox
                v-for="option in metricTypeOptions"
                :key="option.value"
                :label="option.value"
              >
                {{ option.label }}
              </el-checkbox>
            </el-checkbox-group>
          </el-form-item>

          <el-form-item>
            <el-button icon="el-icon-refresh" @click="handleReset">
              重置
            </el-button>
            <el-button
              type="success"
              icon="el-icon-download"
              @click="handleExportChart"
              :loading="exportLoading"
            >
              导出图表
            </el-button>
          </el-form-item>
        </el-form>
      </el-card>

      <!-- 图表区域 -->
      <el-card class="chart-card" shadow="never" v-loading="chartLoading">
        <div slot="header" class="card-header">
          <span>IP访问统计趋势</span>
        </div>
        <div id="ipStatsChart" style="width: 100%; height: 450px;"></div>
      </el-card>

      <!-- 访问明细 -->
      <el-card class="table-card" shadow="never">
        <div slot="header" class="card-header">
          <span>访问明细</span>
        </div>

        <!-- 查询表单 -->
        <el-form :inline="true" :model="pageQuery" style="margin-bottom: 15px;">
          <el-form-item label="用户ID">
            <el-input
              v-model="pageQuery.userId"
              placeholder="请输入用户ID"
              clearable
              style="width: 200px;"
              @keyup.enter.native="handlePageQuery"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handlePageQuery">查询</el-button>
            <el-button icon="el-icon-refresh-left" @click="handleResetPageQuery">重置</el-button>
          </el-form-item>
        </el-form>

        <!-- 数据表格 -->
        <el-table
          :data="tableData.list"
          border
          v-loading="tableLoading"
          style="width: 100%;"
        >
          <el-table-column prop="id" label="ID" width="180" align="center" />
          <el-table-column prop="userId" label="用户ID" width="120" align="center" />
          <el-table-column prop="realIp" label="真实IP" width="150" align="center" />
          <el-table-column prop="proxyIp" label="代理IP" width="150" align="center" />
          <el-table-column prop="type" label="页面类型" width="120" align="center">
            <template slot-scope="scope">
              <el-tag :type="getPageTypeTagType(scope.row.type)">
                {{ getPageTypeName(scope.row.type) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="createTime" label="访问时间" width="180" align="center" />
        </el-table>

        <!-- 分页 -->
        <el-pagination
          :current-page="pageQuery.page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pageQuery.limit"
          :total="tableData.totalCount"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          style="margin-top: 20px; text-align: right;"
        />
      </el-card>
    </div>
  </el-card>
</template>

<script>
import * as echarts from 'echarts'
import { getChartData, getPageData } from '@/api/ipStatistics'

export default {
  name: 'IpStatistics',
  data() {
    return {
      // 统一查询条件
      queryForm: {
        startDate: '',
        endDate: '',
        pageTypes: [1, 2],
        metricTypes: ['COUNT']
      },

      // 日期范围
      dateRange: [],

      // 日期选择器配置
      pickerOptions: {
        shortcuts: [
          {
            text: '最近一周',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: '最近一个月',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: '最近三个月',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
              picker.$emit('pick', [start, end])
            }
          }
        ]
      },

      // 分页配置
      pageQuery: {
        page: 1,
        limit: 10,
        userId: '' // 用户ID查询条件
      },

      // 页面类型选项
      pageTypeOptions: [
        { label: '首页', value: 1 },
        { label: '视频详情', value: 2 },
        { label: '支付记录', value: 3 }
      ],

      // 指标类型选项
      metricTypeOptions: [
        { label: '访问次数', value: 'COUNT' },
        { label: '独立IP数', value: 'IP' }
      ],

      // 图表数据
      chartData: null,

      // 表格数据
      tableData: {
        list: [],
        totalCount: 0,
        totalPage: 0,
        currPage: 1
      },

      // 加载状态
      chartLoading: false,
      tableLoading: false,
      exportLoading: false,

      // ECharts实例
      chartInstance: null
    }
  },

  mounted() {
    // 初始化默认日期范围（最近7天）
    this.initDefaultDateRange()

    // 加载查询条件缓存
    this.loadQueryCache()

    // 初始化图表
    this.initChart()

    // 查询数据
    this.handleQuery()

    // 监听窗口大小变化
    window.addEventListener('resize', this.handleResize)
  },

  beforeDestroy() {
    // 销毁图表实例
    if (this.chartInstance) {
      this.chartInstance.dispose()
      this.chartInstance = null
    }

    // 移除事件监听
    window.removeEventListener('resize', this.handleResize)
  },

  watch: {
    // 监听页面类型变化
    'queryForm.pageTypes': {
      handler(newVal, oldVal) {
        // 避免初始化时触发
        if (oldVal !== undefined && newVal.length > 0) {
          this.pageQuery.page = 1 // 重置到第一页
          this.handleQuery()
        }
      },
      deep: true
    },

    // 监听统计指标变化
    'queryForm.metricTypes': {
      handler(newVal, oldVal) {
        // 避免初始化时触发
        if (oldVal !== undefined && newVal.length > 0) {
          this.handleChartQuery()
        }
      },
      deep: true
    }
  },

  methods: {
    // 初始化默认日期范围
    initDefaultDateRange() {
      const end = new Date()
      const start = new Date()
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)

      this.dateRange = [this.formatDate(start), this.formatDate(end)]
      this.queryForm.startDate = this.dateRange[0]
      this.queryForm.endDate = this.dateRange[1]
    },

    // 格式化日期
    formatDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    // 日期范围变化
    onDateRangeChange(val) {
      if (val && val.length === 2) {
        this.queryForm.startDate = val[0]
        this.queryForm.endDate = val[1]
        // 日期变化后触发查询
        this.handleQuery()
      } else {
        this.queryForm.startDate = ''
        this.queryForm.endDate = ''
      }
    },

    // 初始化图表
    initChart() {
      const chartDom = document.getElementById('ipStatsChart')
      if (chartDom) {
        this.chartInstance = echarts.init(chartDom)
      }
    },

    // 统一查询方法
    handleQuery() {
      this.handleChartQuery()
      this.handlePageQuery()
    },

    // 查询图表数据
    async handleChartQuery() {
      // 验证查询条件
      if (!this.queryForm.startDate || !this.queryForm.endDate) {
        this.$message.warning('请选择日期范围')
        return
      }

      if (this.queryForm.pageTypes.length === 0) {
        this.$message.warning('请至少选择一个页面类型')
        return
      }

      if (this.queryForm.metricTypes.length === 0) {
        this.$message.warning('请至少选择一个统计指标')
        return
      }

      this.chartLoading = true

      try {
        const { data } = await getChartData({
          startDate: this.queryForm.startDate,
          endDate: this.queryForm.endDate,
          pageTypes: this.queryForm.pageTypes,
          metricTypes: this.queryForm.metricTypes
        })

        if (data.code === 0) {
          this.chartData = data.data
          this.renderChart()
          this.saveQueryCache()
        } else {
          this.$message.error(data.msg || '查询失败')
        }
      } catch (error) {
        console.error('查询图表数据失败:', error)
        this.$message.error('查询失败，请稍后重试')
      } finally {
        this.chartLoading = false
      }
    },

    // 渲染图表
    renderChart() {
      if (!this.chartData || !this.chartInstance) return

      const option = {
        title: {
          text: 'IP访问统计趋势',
          left: 'center',
          top: 10
        },

        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          }
        },

        legend: {
          data: this.chartData.series.map(s => s.name),
          top: 45,
          type: 'scroll'
        },

        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          top: '100px',
          containLabel: true
        },

        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: this.chartData.dates,
          axisLabel: {
            rotate: 45,
            formatter: (value) => {
              return value.substring(5)
            }
          }
        },

        yAxis: {
          type: 'value',
          name: '数量',
          minInterval: 1
        },

        series: this.chartData.series.map((item, index) => ({
          name: item.name,
          type: 'line',
          data: item.data,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: {
            width: 2
          },
          itemStyle: {
            color: this.getSeriesColor(item.name, index)
          }
        })),

        toolbox: {
          feature: {
            saveAsImage: {
              title: '保存为图片'
            },
            dataZoom: {
              title: {
                zoom: '区域缩放',
                back: '还原缩放'
              }
            },
            restore: {
              title: '还原'
            }
          },
          right: 20
        },

        dataZoom: [
          {
            type: 'inside',
            start: 0,
            end: 100
          }
        ]
      }

      this.chartInstance.setOption(option, true)
    },

    // 获取系列颜色
    getSeriesColor(name, index) {
      const colorMap = {
        '首页-访问次数': '#5470c6',
        '首页-独立IP数': '#91cc75',
        '视频详情-访问次数': '#fac858',
        '视频详情-独立IP数': '#ee6666',
        '支付记录-访问次数': '#73c0de',
        '支付记录-独立IP数': '#3ba272'
      }

      return colorMap[name] || ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272'][index % 6]
    },

    // 重置查询
    handleReset() {
      this.initDefaultDateRange()
      this.queryForm.pageTypes = [1, 2, 3]
      this.queryForm.metricTypes = ['COUNT']
      this.pageQuery.page = 1
      this.handleQuery()
    },

    // 导出图表
    handleExportChart() {
      if (!this.chartInstance) {
        this.$message.warning('请先查询图表数据')
        return
      }

      try {
        const url = this.chartInstance.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff'
        })

        const link = document.createElement('a')
        link.href = url
        link.download = `IP访问统计_${new Date().toLocaleDateString()}.png`
        link.click()

        this.$message.success('导出成功')
      } catch (error) {
        console.error('导出失败:', error)
        this.$message.error('导出失败')
      }
    },

    // 查询分页数据
    async handlePageQuery() {
      if (this.queryForm.pageTypes.length === 0) {
        this.$message.warning('请至少选择一个页面类型')
        return
      }

      this.tableLoading = true

      try {
        const params = {
          page: this.pageQuery.page,
          limit: this.pageQuery.limit,
          pageTypes: this.queryForm.pageTypes
        }

        // 使用日期范围的开始日期作为查询日期
        if (this.queryForm.startDate) {
          params.startDate = this.queryForm.startDate
        }
        if (this.queryForm.endDate) {
          params.endDate = this.queryForm.endDate
        }

        // 添加 userId 查询条件
        if (this.pageQuery.userId) {
          params.userId = this.pageQuery.userId
        }

        const { data } = await getPageData(params)

        if (data.code === 0) {
          this.tableData = data.data
        } else {
          this.$message.error(data.msg || '查询失败')
        }
      } catch (error) {
        console.error('查询分页数据失败:', error)
        this.$message.error('查询失败，请稍后重试')
      } finally {
        this.tableLoading = false
      }
    },

    // 每页条数变化
    handleSizeChange(val) {
      this.pageQuery.limit = val
      this.pageQuery.page = 1
      this.handlePageQuery()
    },

    // 页码变化
    handleCurrentChange(val) {
      this.pageQuery.page = val
      this.handlePageQuery()
    },

    // 重置明细查询条件
    handleResetPageQuery() {
      this.pageQuery.userId = ''
      this.pageQuery.page = 1
      this.handlePageQuery()
    },

    // 获取页面类型名称
    getPageTypeName(type) {
      const map = {
        1: '首页',
        2: '视频详情',
        3: '支付记录'
      }
      return map[type] || '未知'
    },

    // 获取页面类型标签颜色
    getPageTypeTagType(type) {
      const typeMap = {
        1: 'primary',
        2: 'success',
        3: 'warning'
      }
      return typeMap[type] || ''
    },

    // 窗口大小变化
    handleResize() {
      if (this.chartInstance) {
        this.chartInstance.resize()
      }
    },

    // 保存查询条件缓存
    saveQueryCache() {
      try {
        localStorage.setItem('ipStatsQuery', JSON.stringify({
          dateRange: this.dateRange,
          pageTypes: this.queryForm.pageTypes,
          metricTypes: this.queryForm.metricTypes
        }))
      } catch (error) {
        console.error('保存缓存失败:', error)
      }
    },

    // 加载查询条件缓存
    loadQueryCache() {
      try {
        const cache = localStorage.getItem('ipStatsQuery')
        if (cache) {
          const data = JSON.parse(cache)
          if (data.dateRange && data.dateRange.length === 2) {
            this.dateRange = data.dateRange
            this.queryForm.startDate = data.dateRange[0]
            this.queryForm.endDate = data.dateRange[1]
          }
          if (data.pageTypes) {
            this.queryForm.pageTypes = data.pageTypes
          }
          if (data.metricTypes) {
            this.queryForm.metricTypes = data.metricTypes
          }
        }
      } catch (error) {
        console.error('加载缓存失败:', error)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.ip-statistics-page {
  .filter-card,
  .chart-card,
  .table-card {
    margin-bottom: 20px;
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
    font-weight: 500;
  }

  .el-form {
    ::v-deep .el-form-item {
      margin-bottom: 12px;
    }

    ::v-deep .el-checkbox-group {
      .el-checkbox {
        margin-right: 15px;
      }
    }
  }

  #ipStatsChart {
    min-height: 450px;
  }

  .el-table {
    margin-top: 15px;

    ::v-deep .el-table__header th {
      background-color: #f5f7fa;
      color: #606266;
      font-weight: 500;
    }
  }
}
</style>
